import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { EnvService } from '../../env.service';

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private router = inject(Router);
  private envService = inject(EnvService);
  private measurementId: string | null = null;

  constructor() {
    this.measurementId = this.envService.GoogleAnalyticsId || null;
    console.log('AnalyticsService: constructor called. Measurement ID:', this.measurementId);
    if (this.measurementId) {
      this.initGA();
      this.trackPageViews();
    } else {
      console.warn('AnalyticsService: Measurement ID is empty. GA tracking is disabled.');
    }
  }

  private initGA() {
    console.log('AnalyticsService: Initializing GA script tag for ID:', this.measurementId);
    // Inject gtag script tag dynamically
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    document.head.appendChild(script);

    // Initialize global gtag function
    const win = window as any;
    win.dataLayer = win.dataLayer || [];
    win.gtag = function () {
      win.dataLayer.push(arguments);
    };
    win.gtag('js', new Date());
    win.gtag('config', this.measurementId, {
      send_page_view: false, // We track manually to get clean paths and titles
    });
  }

  private trackPageViews() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Delay slightly to allow TemplatePageTitleStrategy to set document.title
        setTimeout(() => {
          const url = event.urlAfterRedirects || event.url;
          // Strictly exclude Checkout module routes from analytics tracking
          if (url.includes('/secure-checkout')) {
            console.log('AnalyticsService: Excluding checkout route from page view tracking:', url);
            return;
          }
          this.trackPageView(url);
        }, 100);
      });
  }

  public trackPageView(url: string) {
    if (!this.measurementId) return;
    // Extra safety guard: do not track if route is in checkout
    if (url.includes('/secure-checkout')) {
      return;
    }
    const win = window as any;
    if (win.gtag) {
      win.gtag('event', 'page_view', {
        page_path: url,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }

  public trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
    if (!this.measurementId) return;

    // Extra safety guard: do not track events if we are on a checkout route
    const currentUrl = this.router.url;
    if (currentUrl.includes('/secure-checkout')) {
      console.warn(`AnalyticsService: Excluded event tracking on checkout page: ${eventName}`);
      return;
    }

    const win = window as any;
    if (win.gtag) {
      // Filter out undefined values to keep parameters clean
      const cleanedParams = Object.keys(eventParams).reduce((acc, key) => {
        if (eventParams[key] !== undefined && eventParams[key] !== null) {
          acc[key] = eventParams[key];
        }
        return acc;
      }, {} as Record<string, any>);

      win.gtag('event', eventName, cleanedParams);
    }
  }
}
